import { useState, useEffect, useRef, useCallback } from 'react'
import { usePublisherAuth } from '../../context/PublisherAuthContext'
import {
  User,
  Mail,
  Globe,
  Building,
  Shield,
  Key,
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  Save,
} from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export default function PublisherSettingsPage() {
  const { publisher, token } = usePublisherAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [profile, setProfile] = useState({
    display_name: '',
    slug: '',
    email: '',
    website_url: '',
    bio: '',
    type: 'individual' as 'individual' | 'organization',
  })

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  // Slug check state
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle')
  const slugCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (publisher) {
      setProfile({
        display_name: publisher.display_name || '',
        slug: publisher.slug || '',
        email: publisher.email || '',
        website_url: publisher.website_url || '',
        bio: '',
        type: publisher.type as 'individual' | 'organization',
      })
    }
  }, [publisher])

  const checkSlugAvailability = useCallback(async (slug: string) => {
    if (!slug || slug === publisher?.slug) {
      setSlugStatus('idle')
      return
    }

    // Validate format: lowercase alphanumeric and hyphens only
    if (!/^[a-z0-9-]+$/.test(slug)) {
      setSlugStatus('invalid')
      return
    }

    setSlugStatus('checking')

    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/publisher/slug-check?slug=${encodeURIComponent(slug)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        setSlugStatus('invalid')
        return
      }

      const data = await response.json()
      setSlugStatus(data.available ? 'available' : 'taken')
    } catch {
      setSlugStatus('invalid')
    }
  }, [token, publisher?.slug])

  const handleSlugChange = (value: string) => {
    const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setProfile((prev) => ({ ...prev, slug: sanitized }))

    if (slugCheckTimer.current) {
      clearTimeout(slugCheckTimer.current)
    }

    if (!sanitized || sanitized === publisher?.slug) {
      setSlugStatus('idle')
      return
    }

    if (!/^[a-z0-9-]+$/.test(sanitized)) {
      setSlugStatus('invalid')
      return
    }

    slugCheckTimer.current = setTimeout(() => {
      checkSlugAvailability(sanitized)
    }, 500)
  }

  const handleProfileSave = async () => {
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/v1/publisher/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          display_name: profile.display_name,
          slug: profile.slug,
          email: profile.email,
          website_url: profile.website_url,
          bio: profile.bio,
          type: profile.type,
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Failed to update profile' }))
        throw new Error(err.message || 'Failed to update profile')
      }

      const updated = await response.json()
      if (updated.publisher) {
        localStorage.setItem('xarlie_publisher', JSON.stringify(updated.publisher))
      }

      setSuccess('Profile updated successfully')
      setSlugStatus('idle')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleRequestVerification = async () => {
    setError('')
    setSuccess('')
    setLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/v1/publisher/me/request-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Failed to request verification' }))
        throw new Error(err.message || 'Failed to request verification')
      }

      const result = await response.json()
      setSuccess(result.message || 'Verification request submitted')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to request verification')
    } finally {
      setLoading(false)
    }
  }

  const [apiKey, setApiKey] = useState<string | null>(null)

  const handleGenerateAPIKey = async () => {
    setError('')
    setSuccess('')
    setLoading(true)
    setApiKey(null)

    try {
      const response = await fetch(`${API_BASE_URL}/v1/publisher/me/api-keys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ label: 'default' }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Failed to generate API key' }))
        throw new Error(err.message || 'Failed to generate API key')
      }

      const result = await response.json()
      setApiKey(result.api_key)
      setSuccess('API key generated. Copy it now — it will not be shown again.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate API key')
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    setError('')
    setSuccess('')

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match')
      return
    }

    if (passwordData.new_password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE_URL}/v1/publisher/me/password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password,
        }),
      })

      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Failed to change password' }))
        throw new Error(err.message || 'Failed to change password')
      }

      setSuccess('Password changed successfully')
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your publisher profile and account</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2 text-green-700">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {/* Profile Settings */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          <User className="w-5 h-5 inline mr-2" />
          Profile Information
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Type
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setProfile((prev) => ({ ...prev, type: 'individual' }))}
                className={`flex-1 p-3 border rounded-lg flex items-center justify-center space-x-2 ${
                  profile.type === 'individual'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <User className="w-5 h-5" />
                <span>Individual</span>
              </button>
              <button
                type="button"
                onClick={() => setProfile((prev) => ({ ...prev, type: 'organization' }))}
                className={`flex-1 p-3 border rounded-lg flex items-center justify-center space-x-2 ${
                  profile.type === 'organization'
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Building className="w-5 h-5" />
                <span>Organization</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={profile.display_name}
                onChange={(e) =>
                  setProfile((prev) => ({ ...prev, display_name: e.target.value }))
                }
                className="input pl-10 w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Slug
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 select-none">
                xarlie.io/creators/
              </span>
              <input
                type="text"
                value={profile.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                className="input pl-[9.5rem] pr-10 w-full"
                placeholder="your-slug"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {slugStatus === 'checking' && (
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                )}
                {slugStatus === 'available' && (
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                )}
                {slugStatus === 'taken' && (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                {slugStatus === 'invalid' && (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
            </div>
            {slugStatus === 'taken' && (
              <p className="text-sm text-red-600 mt-1">This slug is already taken</p>
            )}
            {slugStatus === 'invalid' && (
              <p className="text-sm text-red-600 mt-1">Slug must be lowercase alphanumeric and hyphens only</p>
            )}
            {slugStatus === 'available' && (
              <p className="text-sm text-green-600 mt-1">This slug is available</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile((prev) => ({ ...prev, email: e.target.value }))}
                className="input pl-10 w-full"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={profile.website_url}
                onChange={(e) =>
                  setProfile((prev) => ({ ...prev, website_url: e.target.value }))
                }
                className="input pl-10 w-full"
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bio
            </label>
            <textarea
              value={profile.bio}
              onChange={(e) => setProfile((prev) => ({ ...prev, bio: e.target.value }))}
              className="input w-full h-24"
              placeholder="Tell us about yourself or your organization..."
            />
          </div>

          <div className="flex justify-end">
            <button onClick={handleProfileSave} disabled={loading || slugStatus === 'taken'} className="btn btn-primary">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>
      </div>

      {/* Verification Status */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          <Shield className="w-5 h-5 inline mr-2" />
          Verification Status
        </h2>
        {publisher?.verified ? (
          <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-lg">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div>
              <p className="font-medium text-green-800">Verified Publisher</p>
              <p className="text-sm text-green-600">
                Your account has been verified. Your packages display the verified badge.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 rounded-lg">
              <AlertCircle className="w-8 h-8 text-yellow-500" />
              <div>
                <p className="font-medium text-yellow-800">Not Verified</p>
                <p className="text-sm text-yellow-600">
                  Verify your account to get a verified badge on your packages.
                </p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              <p className="mb-2">To become verified, you need to:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Have at least one published and approved package</li>
                <li>Verify your email address</li>
                <li>Complete your profile information</li>
              </ul>
            </div>
            <button onClick={handleRequestVerification} disabled={loading} className="btn btn-secondary">
              {loading ? 'Submitting...' : 'Request Verification'}
            </button>
          </div>
        )}
      </div>

      {/* Password Change */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          <Key className="w-5 h-5 inline mr-2" />
          Change Password
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Password
            </label>
            <input
              type="password"
              value={passwordData.current_password}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, current_password: e.target.value }))
              }
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password
            </label>
            <input
              type="password"
              value={passwordData.new_password}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, new_password: e.target.value }))
              }
              className="input w-full"
              placeholder="At least 8 characters"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password
            </label>
            <input
              type="password"
              value={passwordData.confirm_password}
              onChange={(e) =>
                setPasswordData((prev) => ({ ...prev, confirm_password: e.target.value }))
              }
              className="input w-full"
            />
          </div>
          <div className="flex justify-end">
            <button
              onClick={handlePasswordChange}
              disabled={loading || !passwordData.current_password || !passwordData.new_password}
              className="btn btn-secondary"
            >
              Change Password
            </button>
          </div>
        </div>
      </div>

      {/* API Keys */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">API Keys</h2>
        <p className="text-gray-500 mb-4">
          Manage API keys for programmatic access to your publisher account.
        </p>
        {apiKey && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-medium text-green-800 mb-1">Your new API key:</p>
            <code className="block p-2 bg-white border rounded text-sm font-mono break-all select-all">
              {apiKey}
            </code>
            <p className="text-xs text-green-600 mt-1">Copy this key now. It will not be shown again.</p>
          </div>
        )}
        <button onClick={handleGenerateAPIKey} disabled={loading} className="btn btn-secondary">
          {loading ? 'Generating...' : 'Generate New API Key'}
        </button>
      </div>
    </div>
  )
}
